import React from 'react';
import Navbar from '@/common/Navbar/Navbar';
import Sidebar from '@/common/Sidebar/Sidebar';
import EnvironmentBanner from '@/common/EnvironmentBanner/EnvironmentBanner';

/**
 * ARCHITECTURAL NOTE - CRITICAL FOR ALL DEVELOPMENT:
 * 
 * This application uses a HYBRID UI component approach:
 * 
 * BOOTSTRAP for layout components:
 * - Row, Col, Container, Card, Button, Form (import as BootstrapForm)
 * - Use Bootstrap grid syntax: <Col md={4}>
 * 
 * ANT DESIGN for specific components only:
 * - Select, Modal, Tooltip, Checkbox, Icons
 * - Use Ant Design syntax: <Col span={8}>
 * 
 * NEVER MIX: Bootstrap Row/Col with Ant Design Row/Col!
 * This causes layout issues and "destroyed" styling.
 * 
 * For filters: Bootstrap Form + FormItem + Ant Design Select
 * Reference: music-vendor-roles.module.scss for styling
 */
import DashboardPage from '@/pages/dashboard';
import AccountsPage from '@/pages/accounts';
import ClientSupportPage from '@/pages/client-support';
import AlbumsPage from '@/pages/albums';
import ArtistsPage from '@/pages/artists';
import ChannelDashboardPage from '@/pages/channel-dashboard';

import LivePlaysTrackerPage from '@/pages/live-plays-tracker';
import GenresPage from '@/pages/genres';
import RecordLabelsPage from '@/pages/record-labels';
import SongsPage from '@/pages/songs';
import RolesPage from '@/pages/roles';
import ClientRolesPage from '@/pages/client-roles';

import MusicVendorsPage from '@/pages/music-vendors';
import MusicVendorClientsPage from '@/pages/music-vendor-clients';
import MusicVendorRolesPage from '@/pages/music-vendor-roles';
import InstallerVendorsPage from '@/pages/installer-vendors';
import InstallerVendorClientsPage from '@/pages/installer-vendor-clients';
import InstallerVendorLocationsPage from '@/pages/installer-vendor-locations';
import InstallerVendorRolesPage from '@/pages/installer-vendor-roles';
import PendingApprovalsPage from '@/pages/pending-approvals';
import MessageRequestsPage from '@/pages/message-requests';
import TalentRequestsPage from '@/pages/talent-requests';
import TalentsPage from '@/pages/talents';
import TransactionsPage from '@/pages/transactions';
import TrackersPage from '@/pages/trackers';
import DevicesPage from '@/pages/devices';
import ReleasesPage from '@/pages/releases';
import AccountLocationsPage from '@/pages/account-locations';
import LocationZonesPage from '@/pages/location-zones';
import AccountProfile from '@/pages/account-profile';
import AccountSecurity from '@/pages/account-security';
import AccountCompanyProfile from '@/pages/account-company-profile';
import ChannelAdminPage from '@/pages/channel-admin';
import ChannelProgramming from '@/pages/channel-programming-interface';
import ChannelProgrammingOverview from '@/pages/channel-programming-overview';
import TestPage from '@/pages/test';
import UploaderPage from '@/pages/uploader';
import UploaderHistoryPage from '@/pages/uploader-history';
import UploaderHistoryDetailPage from '@/pages/uploader-history-detail';
import ProcessingJobsPage from '@/pages/processing-jobs';
import MusicUploadReviewsPage from '@/pages/music-upload-reviews';
import ProcessingJobDetailPage from '@/pages/processing-job-detail';
import InventoryProductsPage from '@/pages/inventory-products';
import InventoryActivityPage from '@/pages/inventory-activity';
import InventoryShippingPage from '@/pages/inventory-shipping';
import InventoryProductDetailsPage from '@/pages/inventory-product-details';
import InventoryProductInventoryPage from '@/pages/inventory-product-inventory';
import ProductInventoryPage from '@/pages/product-inventory';
import InventoryDeviceAssignmentsPage from '@/pages/inventory-device-assignments';
import InventoryCategoryDetailsPage from '@/pages/inventory-category-details';
import CategoriesPage from '@/pages/categories';
import WearhousePage from '@/pages/wearhouse';
import BrandsPage from '@/pages/brands';
import BrandLocationsPage from '@/pages/brand-locations';
import ClientBrandsPage from '@/pages/client-brands';
import EmployeesPage from '@/pages/employees';
import EmployeeDetailPage from '@/pages/employee-detail';
import EmployeeRolesPage from '@/pages/employee-roles';
import EmployeeDepartmentsPage from '@/pages/employee-departments';
import ActivityHistoryPage from '@/pages/activity-history';
import LLTPage from '@/pages/llt';
import PROsLocationReportsPage from '@/pages/pros-location-reports';
import PricingMusicPlansPage from '@/pages/pricing-music-plans';
import PricingDevicesPage from '@/pages/pricing-devices';
import { Button, Container } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { Toaster } from 'react-hot-toast';
import { sec } from '@/lib/security';
import LoadingOverlay from '@/common/LoadingOverlay/LoadingOverlay';
import Modals from '@/components/Modals';
import './App.scss';
import { BrowserRouter as Router, Route, Routes, Navigate, Outlet, useLocation } from 'react-router-dom';
// ProfileProvider mounts inside SuperAdminAccessGate after GET /sa/super-admin/me
// approves the user — unapproved authenticated users never see Navbar/Sidebar.
import { activityTracker } from './services/activityTracker';
import { clearLocalAuth0State } from '@/lib/authLogout';
import { buildAuth0AuthorizationParams } from '@/lib/auth0AuthorizationParams';
import SuperAdminAccessGate from '@/components/SuperAdminAccessGate';
import RoutePermissionGuard from '@/components/RoutePermissionGuard';
import SaPageViewTracker from '@/features/activity-history/SaPageViewTracker';
import { ActiveBulkOpProgressHost } from '@/features/processing/ActiveBulkOpProgressHost';

const LOGGED_OUT_RELOAD_FLAG = 'kk_logged_out_reloaded';

const RequireAuthRoute: React.FC = () => {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const location = useLocation();

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      void loginWithRedirect({
        appState: {
          returnTo: `${location.pathname}${location.search}${location.hash}`,
        },
        authorizationParams: buildAuth0AuthorizationParams(),
      });
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect, location.pathname, location.search, location.hash]);

  if (isLoading || (!isAuthenticated && !error)) {
    return (
      <Container>
        <LoadingOverlay />
      </Container>
    );
  }

  if (error) {
    console.error('[RequireAuthRoute] Auth error:', error);
    return (
      <Container>
        <div style={{ padding: '16px' }}>Authentication error. Please refresh and try again.</div>
      </Container>
    );
  }

  return <Outlet />;
};

const LoggedOutPage: React.FC = () => {
  const { loginWithRedirect, isLoading, user } = useAuth0();

  React.useEffect(() => {
    clearLocalAuth0State();

    if (isLoading || !user) return;

    const alreadyReloaded =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(LOGGED_OUT_RELOAD_FLAG) === '1';

    if (alreadyReloaded) return;

    try {
      window.sessionStorage.setItem(LOGGED_OUT_RELOAD_FLAG, '1');
    } catch (err) {
      console.warn('[LoggedOutPage] sessionStorage flag set failed:', err);
    }
    console.warn('[LoggedOutPage] cached user detected after logout, reloading');
    window.location.reload();
  }, [isLoading, user]);

  React.useEffect(() => {
    if (!user && !isLoading) {
      try {
        window.sessionStorage.removeItem(LOGGED_OUT_RELOAD_FLAG);
      } catch {
        // best effort
      }
    }
  }, [user, isLoading]);

  const handleSignIn = React.useCallback(() => {
    void loginWithRedirect({
      appState: {
        returnTo: '/dashboard',
      },
      authorizationParams: buildAuth0AuthorizationParams(),
    });
  }, [loginWithRedirect]);

  return (
    <Container style={{ maxWidth: 640, marginTop: 80, textAlign: 'center' }}>
      <h2>You are signed out</h2>
      <p style={{ color: '#666', marginTop: 12 }}>
        Your Super Admin session has ended successfully.
      </p>
      <Button
        type="button"
        variant="primary"
        onClick={handleSignIn}
        disabled={isLoading}
        style={{ marginTop: 20 }}
      >
        Sign In Again
      </Button>
    </Container>
  );
};

const RootRoute: React.FC = () => {
  const location = useLocation();
  const isLoggedOut = React.useMemo(() => {
    const search = new URLSearchParams(location.search);
    return search.get('logged_out') === '1';
  }, [location.search]);

  if (isLoggedOut) {
    return <LoggedOutPage />;
  }

  return <Navigate to="/dashboard" replace />;
};

const AuthenticatedAppShell: React.FC = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);

  React.useEffect(() => {
    document.body.classList.toggle('kk-sidebar-collapsed', isSidebarCollapsed);
    return () => {
      document.body.classList.remove('kk-sidebar-collapsed');
    };
  }, [isSidebarCollapsed]);

  return (
    <main>
      <Navbar
        isSidebarCollapsed={isSidebarCollapsed}
        onSidebarToggle={() => setIsSidebarCollapsed((prev) => !prev)}
      />
      <Sidebar isCollapsed={isSidebarCollapsed} />
      <div className={`main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <EnvironmentBanner />
        <SaPageViewTracker />
        <Toaster />
        <ActiveBulkOpProgressHost />
        <Container>
          <RoutePermissionGuard>
            <Outlet />
          </RoutePermissionGuard>
        </Container>
      </div>
      <Modals />
    </main>
  );
};

// Component that handles auth0 redirect navigation (inside Router context)
const AppContent: React.FC = () => {
  const auth0 = useAuth0();
  const { getAccessTokenSilently, getIdTokenClaims, isAuthenticated } = auth0;

  // Set Auth0 functions and client in global module to reuse it outside a React component
  React.useEffect(() => {
    sec.setAccessTokenSilently(getAccessTokenSilently);
    sec.setIdTokenClaims(getIdTokenClaims);
    sec.setAuth0Client(auth0);

    // Set token getter for activityTracker
    activityTracker.setTokenGetter(async () => {
      return await getAccessTokenSilently({
        authorizationParams: buildAuth0AuthorizationParams(),
      });
    });
  }, [getAccessTokenSilently, getIdTokenClaims, auth0]);

  React.useEffect(() => {
    console.log('[AppContent] Authentication state changed:', { isAuthenticated });
  }, [isAuthenticated]);

  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route element={<RequireAuthRoute />}>
        <Route element={<SuperAdminAccessGate />}>
          <Route element={<AuthenticatedAppShell />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/client-support" element={<ClientSupportPage />} />
                <Route path="/albums" element={<AlbumsPage />} />
                <Route path="/artists" element={<ArtistsPage />} />
                <Route path="/music-channel-programming" element={<ChannelDashboardPage />} />

                <Route path="/live-plays-tracker" element={<LivePlaysTrackerPage />} />
                <Route path="/genres" element={<GenresPage />} />
                <Route path="/record-labels" element={<RecordLabelsPage />} />
                <Route path="/songs" element={<SongsPage />} />
                <Route path="/talent-requests" element={<TalentRequestsPage />} />
                <Route path="/talents" element={<TalentsPage />} />
                <Route path="/transactions" element={<TransactionsPage />} />
                <Route path="/trackers" element={<TrackersPage />} />
                <Route path="/devices" element={<DevicesPage />} />
                <Route path="/releases" element={<ReleasesPage />} />
                <Route path="/client-locations/:companyNumber/locations" element={<AccountLocationsPage />} />
                <Route path="/client-locations/:companyNumber/locations/:locationId/zones" element={<LocationZonesPage />} />
                <Route path="/account/profile" element={<AccountProfile />} />
                <Route path="/account/security" element={<AccountSecurity />} />
                <Route path="/account/company-profile" element={<AccountCompanyProfile />} />
                <Route path="/channel-admin" element={<ChannelAdminPage />} />
                <Route path="/channel-programming-overview" element={<ChannelProgrammingOverview />} />
                <Route path="/channel-programming/:channelId" element={<ChannelProgramming />} />
                <Route path="/test" element={<TestPage />} />
                <Route path="/roles" element={<RolesPage />} />
                <Route path="/client-roles" element={<ClientRolesPage />} />
                <Route path="/music-vendors" element={<MusicVendorsPage />} />
                <Route path="/music-vendors/:vendorNumber/clients" element={<MusicVendorClientsPage />} />
                <Route path="/music-vendor-roles" element={<MusicVendorRolesPage />} />
                <Route path="/installer-vendors" element={<InstallerVendorsPage />} />
                <Route path="/installer-vendors/:vendorNumber/clients" element={<InstallerVendorClientsPage />} />
                <Route path="/installer-vendors/:vendorNumber/locations" element={<InstallerVendorLocationsPage />} />
                <Route path="/installer-vendor-roles" element={<InstallerVendorRolesPage />} />
                <Route path="/pending-approvals" element={<PendingApprovalsPage />} />
                <Route path="/message-requests" element={<MessageRequestsPage />} />
                <Route path="/inventory-products" element={<InventoryProductsPage />} />
                <Route path="/inventory-products/:productId" element={<InventoryProductDetailsPage />} />
                <Route path="/inventory-products/:productId/inventory" element={<InventoryProductInventoryPage />} />
                <Route path="/product-inventory" element={<ProductInventoryPage />} />
                <Route path="/inventory-device-assignments" element={<InventoryDeviceAssignmentsPage />} />
                <Route path="/inventory-activity" element={<InventoryActivityPage />} />
                <Route path="/inventory-shipping" element={<InventoryShippingPage />} />
                <Route path="/brands" element={<BrandsPage />} />
                <Route path="/brands/:brandId/locations" element={<BrandLocationsPage />} />
                <Route path="/accounts/:companyNumber/brands" element={<ClientBrandsPage />} />
                <Route path="/accounts/:companyNumber/locations" element={<AccountLocationsPage />} />
                <Route path="/employees" element={<EmployeesPage />} />
                <Route path="/employees/:employeeId" element={<EmployeeDetailPage />} />
                <Route path="/employee-roles" element={<EmployeeRolesPage />} />
                <Route path="/employee-departments" element={<EmployeeDepartmentsPage />} />
                <Route path="/activity-history" element={<ActivityHistoryPage />} />
                <Route path="/llt" element={<LLTPage />} />
                <Route path="/pros-location-reports" element={<PROsLocationReportsPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/wearhouse" element={<WearhousePage />} />
                <Route path="/inventory-categories/:categoryName" element={<InventoryCategoryDetailsPage />} />
                <Route path="/uploader" element={<UploaderPage />} />
                <Route path="/uploader-history" element={<UploaderHistoryPage />} />
                <Route path="/uploader-history/:trackId" element={<UploaderHistoryDetailPage />} />
                <Route path="/processing-jobs" element={<ProcessingJobsPage />} />
                <Route path="/processing-jobs/:jobId" element={<ProcessingJobDetailPage />} />
                <Route path="/music-upload-reviews" element={<MusicUploadReviewsPage />} />
                <Route path="/pricing-music-plans" element={<PricingMusicPlansPage />} />
                <Route path="/pricing-devices" element={<PricingDevicesPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </Router>
  );
};

export default App;
